import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Chip from "@mui/material/Chip";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import {ReactComponent as ExpandMoreIcon} from "../../icons/ExpandMore24.svg";

import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import AccordionTitle from "../../components/AccordionTitle";

import DrawerAppBar from "../../components/DrawerAppBar";
import ProgressBar from "../../components/ProgressBar";
import Title from "../../components/Title";
import CreatePassDialog from "../../components/faculty/CreatePassDialog";

import { doService } from "../../services/ServiceHelper";
import { getUserByIdQr } from "../../services/UserService";
import {
  StripedTableCell,
  StripedTableHeadCell,
  StripedTableRow,
} from "../../components/StripedTable";
import { getStudentSummary } from "../../services/UserService";
import TableRowNoResults from "../../components/TableRowNoResults";
import { getPassStatusText } from "../../services/PassService";
import {
  getPeriodName,
  isActivitiesEnabled,
  isCourseEnabled,
  isEventEnabled,
  isPassEnabled,
  isScoreEnabled,
} from "../../services/SchoolService";
import {
  getCourseStartTime,
  getCourseEndTime,
} from "../../services/CourseService";
import {
  getEventEndTime,
  getEventStartTime,
} from "../../services/EventService";
import AddQuickScore from "../../components/faculty/AddQuickScore";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

/**
 *
 * @returns receives the request to process the QR code.  Accepts the encrypted code, then forwards to login page for further processing
 */
const ProcessUserQr2 = () => {
  const { code } = useParams();
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [_showProgress, setShowProgress] = useState(false);
  const [_student, setStudent] = useState({});

  const [_dialogCreatePassOpen, setDialogCreatePassOpen] = useState(false);
  const [_passes, setPasses] = useState([]);
  const [_activities, setActivities] = useState([]);
  const [_events, setEvents] = useState([]);
  const [_courses, setCourses] = useState([]);
  const [_scores, setScores] = useState([]);
  const [_createScoreOpen, setCreateScoreOpen] = useState(false);

  useEffect(() => {
    initContent();
  }, []);

  const initContent = () => {
    setShowProgress(true);

    doService(getUserByIdQr(_school, _jwt, code), (result) => {
      const student = result.data;
      setStudent(student);

      setShowProgress(false);
      doService(getStudentSummary(_school, _jwt, student.eid), (results) => {
        setActivities(results.data.activities);
        setPasses(results.data.passes);
        setEvents(results.data.events);
        setCourses(results.data.courses);
        setScores(results.data.scores);
      });
    });
  };

  const handleClickCreatePass = () => {
    setDialogCreatePassOpen(true);
  };

  const handleCreatePassClose = () => {
    setDialogCreatePassOpen(false);
  };

  const handleCreatePassOK = () => {
    setDialogCreatePassOpen(false);
    initContent();
  };

  const handleClickCreateScore = () => {
    setCreateScoreOpen(true);
  };

  const handleCreateScoreClose = () => {
    setCreateScoreOpen(false);
  }

  const handleAddScoreOK = () => {
    setCreateScoreOpen(false);
    initContent();
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Student QR Options" showBack={true} />
        <ProgressBar showProgress={_showProgress} />
        <TableContainer component={Paper} sx={{ mt: 1, mb: 1 }}>
          <Table aria-label="student information" size="small">
            <TableBody>
              <StripedTableRow>
                <StripedTableHeadCell>Student Name</StripedTableHeadCell>
                <StripedTableCell>
                  {_student.first_name} {_student.last_name}
                </StripedTableCell>
              </StripedTableRow>
              <StripedTableRow>
                <StripedTableHeadCell>Student Grade</StripedTableHeadCell>
                <StripedTableCell>{_student.grade}</StripedTableCell>
              </StripedTableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            {isPassEnabled(_school) && (
              <Button
                className="button1"
                sx={{ mt: 2 }}
                variant="contained"
                onClick={handleClickCreatePass}
                title="Create Hall Pass"
                fullWidth
              >
                Create Hall Pass
              </Button>
            )}
          </Grid>
          <Grid item xs={4}>
            {isPassEnabled(_school) && (
              <Button
                className="button2"
                sx={{ mt: 2 }}
                variant="contained"
                onClick={handleClickCreateScore}
                fullWidth
                title="Add Score/Tardy"
              >
                Add Score/Tardy
              </Button>
            )}
          </Grid>
        </Grid>

        {isPassEnabled(_school) && (
          <Accordion defaultExpanded sx={{ mt: 1 }}>
            <AccordionSummary
              className="accordionHeader"
              expandIcon={<ExpandMoreIcon />}
            >
              <AccordionTitle text={`Pass Details`} />
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    <TableRowNoResults data={_passes} />
                    {_passes.map((p) => (
                      <>
                        <StripedTableRow sx={{ borderTop: "2px solid black" }}>
                          <StripedTableCell>Approved:</StripedTableCell>
                          <StripedTableCell>
                            {dayjs(p.approvedAt).format("hh:mm a")}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Type:</StripedTableCell>
                          <StripedTableCell>{p.passTypeName}</StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Status:</StripedTableCell>
                          <StripedTableCell>
                            {getPassStatusText(p.status)}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>From:</StripedTableCell>
                          <StripedTableCell>
                            {p.teacherLastName},{" "}
                            {p.teacherFirstName.substring(0, 1)}
                          </StripedTableCell>
                        </StripedTableRow>
                        {p.toTeacherLastName !== "None" ||
                          (p.toLocationName !== "None" && (
                            <StripedTableRow
                              sx={{ borderBottom: "black solid 2px" }}
                            >
                              <StripedTableCell>To:</StripedTableCell>
                              <StripedTableCell>
                                {p.toTeacherLastName !== "None" && (
                                  <>
                                    {p.toTeacherLastName},{" "}
                                    {p.toTeacherFirstName.substring(0, 1)}
                                  </>
                                )}
                                {p.toLocationName !== "None" && (
                                  <>{p.toLocationName}</>
                                )}
                              </StripedTableCell>
                            </StripedTableRow>
                          ))}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {isActivitiesEnabled(_school) && (
          <Accordion defaultExpanded sx={{ mt: 1 }}>
            <AccordionSummary
              className="accordionHeader"
              expandIcon={<ExpandMoreIcon />}
            >
              <AccordionTitle text={`Activity Details`} />
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    <TableRowNoResults data={_activities} />
                    {_activities.map((activity) => (
                      <StripedTableRow>
                        {activity.locationName === "" && (
                          <StripedTableCell>
                            <span class="activityName">
                              {getPeriodName(_school, activity.period-1)}: -- NONE
                              --
                            </span>
                          </StripedTableCell>
                        )}
                        {activity.locationName !== "" && (
                          <StripedTableCell>
                            <span class="activityName">
                              {getPeriodName(_school, activity.period-1)}:{" "}
                              {activity.name}
                            </span>
                            <Chip
                              label={activity.attendanceStatus}
                              sx={{ ml: 0.5 }}
                            />
                            <br />
                            {activity.teacherLastName},{" "}
                            {activity.teacherFirstName.substring(0, 1)} -{" "}
                            {activity.locationName}
                          </StripedTableCell>
                        )}
                      </StripedTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {isScoreEnabled(_school) && (
          <Accordion defaultExpanded sx={{ mt: 1 }}>
            <AccordionSummary
              className="accordionHeader"
              expandIcon={<ExpandMoreIcon />}
            >
              <AccordionTitle text={`Today's Scores/Tardies`} />
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    <TableRowNoResults data={_scores} />
                    {_scores.map((score) => (
                      <>
                        <StripedTableRow sx={{ borderTop: "2px solid black" }}>
                          <StripedTableCell>Created:</StripedTableCell>
                          <StripedTableCell>
                            {dayjs(score.dateCreated).format("hh:mm a")}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Category</StripedTableCell>
                          <StripedTableCell>
                            {score.categoryName}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Consequence</StripedTableCell>
                          <StripedTableCell>
                            {score.consequenceName}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Teacher</StripedTableCell>
                          <StripedTableCell>
                            {score.teacherLastName}
                          </StripedTableCell>
                        </StripedTableRow>
                      </>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {isCourseEnabled(_school) && (
          <Accordion defaultExpanded sx={{ mt: 1 }}>
            <AccordionSummary
              className="accordionHeader"
              expandIcon={<ExpandMoreIcon />}
            >
              <AccordionTitle text={`Today's Courses`} />
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    <TableRowNoResults data={_courses} />
                    {_courses.map((c) => (
                      <>
                        <StripedTableRow sx={{ borderTop: "2px solid black" }}>
                          <StripedTableCell>Period:</StripedTableCell>
                          <StripedTableCell>
                            {getCourseStartTime(_school, c)}-
                            {getCourseEndTime(_school, c)}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Name:</StripedTableCell>
                          <StripedTableCell>{c.name}</StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Location:</StripedTableCell>
                          <StripedTableCell>
                            {c.schoolLocationName}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Proctor:</StripedTableCell>
                          <StripedTableCell>
                            {c.last_name}, {c.first_name.substring(0, 1)}
                          </StripedTableCell>
                        </StripedTableRow>
                      </>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {isEventEnabled(_school) && (
          <Accordion defaultExpanded sx={{ mt: 1 }}>
            <AccordionSummary
              className="accordionHeader"
              expandIcon={<ExpandMoreIcon />}
            >
              <AccordionTitle text={`Today's Events`} />
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableBody>
                    <TableRowNoResults data={_events} />
                    {_events.map((e) => (
                      <>
                        <StripedTableRow sx={{ borderTop: "2px solid black" }}>
                          <StripedTableCell>Time:</StripedTableCell>
                          <StripedTableCell>
                            {getEventStartTime(e)}-{getEventEndTime(e)}
                          </StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Title:</StripedTableCell>
                          <StripedTableCell>{e.title}</StripedTableCell>
                        </StripedTableRow>
                        <StripedTableRow>
                          <StripedTableCell>Proctor:</StripedTableCell>
                          <StripedTableCell>
                            {e.teacherLastName},{" "}
                            {e.teacherFirstName.substring(0, 1)}
                          </StripedTableCell>
                        </StripedTableRow>
                      </>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}
      </Container>

      <CreatePassDialog
        initialStudents={[_student.eid]}
        open={_dialogCreatePassOpen}
        onClose={handleCreatePassClose}
        onOK={handleCreatePassOK}
      />

      <Dialog
        open={_createScoreOpen}
        onClose={handleCreateScoreClose}
        maxWidth={"md"}
        fullWidth={true}
      >
        <DialogTitle>Add Score/Tardy</DialogTitle>
        <DialogContent>
          <AddQuickScore jwt={_jwt} school={_school} showPrintReceipt={false} initialStudents={[_student.eid]} onOK={handleAddScoreOK} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProcessUserQr2;
